import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Paper,
  AppBar,
  Toolbar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';
import heroBg from '../assets/sydney-harbour.jpg';
import DesignModuleGrid from './DesignModuleGrid';

export default function EngineeringTools() {
  const navigate = useNavigate();

  const toolFeatures = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Fast Calculations',
      description: 'Instant results for structural design calculations'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40 }} />,
      title: 'Cloud-Based',
      description: 'Access your tools from anywhere, anytime'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Standards Compliant',
      description: 'Built to meet Australian Standards requirements'
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: 'Professional Support',
      description: 'Expert assistance when you need it'
    }
  ];

  return (
    <>
      {/* Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 10,
        }} 
        elevation={0}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* Clickable Logo that links to Home */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => navigate('/')}
            >
              <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
              <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
            </Box>
          </Box>
          
          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2, mr: 3 }}>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/consulting')}
            >
              CONSULTING SERVICES
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/engineering-tools')}
            >
              ENGINEERING TOOLS
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#about');
                setTimeout(() => {
                  document.getElementById('about')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              ABOUT US
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#contact');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              CONTACT
            </Button>
          </Box>
          
          <Button 
            color="primary" 
            variant="contained" 
            sx={{ 
              fontWeight: 600,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
              }
            }}
            onClick={() => navigate('/signin')}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section with Background Image */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 500, md: 600 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(20, 40, 80, 0.7)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, color: '#fff', textAlign: 'center' }}>
          <Fade in timeout={1200}>
            <Box>
              <Typography variant="h2" fontWeight={700} gutterBottom color="#fff" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Smart Structural Design Tools
              </Typography>
              <Typography variant="h5" color="#f5f5f5" paragraph sx={{ mb: 4, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Cloud-based professional structural engineering tools built for Australian Standards compliance
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Tools Overview Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6 }}>
          Why do you need these tools?
        </Typography>
        
        <Grid container spacing={4}>
          {toolFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3} sx={{ height: '100%', borderRadius: 3, textAlign: 'center' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Design Modules Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6 }}>
            Design Modules
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
            Access our comprehensive suite of structural design tools. Each module is designed to streamline your workflow 
            and ensure compliance with Australian Standards.
          </Typography>
          
          <DesignModuleGrid />
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                {/* Company name in text instead of logo */}
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'white' }}>
                  Gemistruct
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
                  Professional consulting and cloud-based engineering tools for Australian structural engineering industry.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                   <Chip label="CPEng" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                   <Chip label="RPEQ" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                   <Chip label="NER" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                 </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Quick Links</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/')}
                >
                  Home
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/consulting')}
                >
                  Consulting Services
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/signin')}
                >
                  Client Portal
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: 1, borderColor: 'grey.800', pt: 3, mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'grey.100' }}>
              Copyright © {new Date().getFullYear()} GEMISTRUCT PTY LTD ABN: 80 684 710 594{' '}
              <Button 
                color="inherit" 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.875rem',
                  color: 'grey.100',
                  '&:hover': { color: 'white' },
                  p: 0,
                  minWidth: 'auto'
                }}
                onClick={() => navigate('/privacy')}
              >
                Privacy
              </Button>
              {' '}|{' '}
              <Button 
                color="inherit" 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.875rem',
                  color: 'grey.100',
                  '&:hover': { color: 'white' },
                  p: 0,
                  minWidth: 'auto'
                }}
                onClick={() => navigate('/terms')}
              >
                Terms & Conditions
              </Button>
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
} 
